import React from "react";
import { Route, BrowserRouter,Routes as ReactDomRoutes, useLocation } from "react-router-dom";

import { StackHandler, StackTheme } from "@stackframe/react";
import { stackClientApp } from "../stack";
import ScrollToTop from "./ScrollToTop";
import SiteHeader from "@/containers/SiteHeader";
import Footer from "@/shared/Footer/Footer";
import FooterNav from "@/components/FooterNav";
import useWindowSize from "@/hooks/useWindowResize";
import Page404 from "@/containers/Page404/Page404";
// import AccountPage from "@/containers/AccountPage/AccountPage";
import Loader from "@/shared/FullScreenLoader/Loading";
import PageHome2 from "@/containers/PageHome/PageHome2";
import AdboardDetailPage from "@/containers/ListingDetailPage/AdboardDetailPage"
import CampaignNew from "@/containers/Campaign/CampaignNew";
import PublisherPage from "@/containers/PublisherPage/PublisherPage";

// Lazy imports
const FAQ = React.lazy(() => import("@/containers/PageHome/FAQ"));
const Campaigns = React.lazy(() => import("@/containers/Campaign/Campaigns"));
const ManageCampaignPage = React.lazy(() => import("@/containers/Campaign/ManageCampaignPage"));
const WishlistsPage = React.lazy(() => import("@/containers/WishlistsPage/WishlistsPage"));

// Lazy imports
// const PageHome2 = React.lazy(() => import("@/containers/PageHome/PageHome2"));
const Sitemap = React.lazy(() => import("@/containers/sitemap/Sitemap"));
const PageHomePublisher = React.lazy(() => import("@/containers/PageHome/PageHomePublisher"));
const PartnerSolutions = React.lazy(() => import("@/containers/PublisherPage/PartnerPage"));
const ListingStayPage = React.lazy(() => import("@/containers/ListingStayPage/ListingStayPage"));
const AdboardMapPage = React.lazy(() => import("@/containers/ListingStayPage/AdboardMapPage"));
const PayDonePage = React.lazy(() => import("@/containers/PayPage/PayDonePage"));
const PayDoneCampaignPage = React.lazy(() => import("@/containers/PayPage/PayDoneCampaignPage"));
const PageContact = React.lazy(() => import("@/containers/PageContact/PageContact"));
const PageAbout = React.lazy(() => import("@/containers/PageAbout/PageAbout"));
const PageSubcription = React.lazy(() => import("@/containers/PageHome/PageSubcription"));
const BookingsPage = React.lazy(() => import("@/containers/Bookings/Bookings"));
const BookingDetails = React.lazy(() => import("@/containers/Bookings/BookingDetailsPage").then(m => ({ default: m.BookingDetails })));
const EnquiriesPage = React.lazy(() => import("@/containers/Bookings/EnquiriesPage"));
const MediaAssetsPage = React.lazy(() => import("@/containers/MediaAssetsPage/MediaAssetsPage"));
const MediaAsset = React.lazy(() => import("@/containers/MediaAssetsPage/MediaAsset/MediaAsset"));
const BlogPage = React.lazy(() => import("@/containers/BlogPage/BlogPage"));
const BlogSingle = React.lazy(() => import("@/containers/BlogPage/BlogSingle"));
const Blogs = React.lazy(() => import("@/containers/BlogPage/Blogs"));
const NotificationsPage = React.lazy(() => import("@/containers/NotificationsPage/NotificationsPage"));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense fallback={<Loader/>}>
    {children}
  </React.Suspense>
);

function HandlerRoutes() {
  const location = useLocation();
  return (
    <div className="max-w-screen-xl mx-auto py-8">
      <StackHandler app={stackClientApp} location={location.pathname} fullPage />
    </div>
  );
}

export function PageRoutes() {

  return (
    <ReactDomRoutes>
      <Route path="/handler/*" element={<HandlerRoutes />} />

      {/* Home */}
      <Route path="/" element={<SuspenseWrapper><PageHome2 /></SuspenseWrapper>} />
      <Route path="/billboard-booking" element={<SuspenseWrapper><PageHome2 /></SuspenseWrapper>} />
      <Route path="/sitemap" element={<SuspenseWrapper><Sitemap /></SuspenseWrapper>} />

      {/* Publisher */}
      <Route path="/publisher/*" element={<SuspenseWrapper><PageHomePublisher /></SuspenseWrapper>} />
      <Route path="/publisher/:workspaceKey/*" element={<PublisherPage />} />
      <Route path="/partner/*" element={<SuspenseWrapper><PartnerSolutions /></SuspenseWrapper>} />

      {/* Listings */}
      <Route path="/listing-stay/*" element={<SuspenseWrapper><ListingStayPage /></SuspenseWrapper>} />
      <Route path="/listing-stay-map/*" element={<SuspenseWrapper><AdboardMapPage /></SuspenseWrapper>} />
      <Route path="/adboards/*" element={<SuspenseWrapper><AdboardMapPage /></SuspenseWrapper>} />
      <Route path="/billboards/:location/*" element={<SuspenseWrapper><AdboardMapPage /></SuspenseWrapper>} />
      <Route path="/billboards/*" element={<SuspenseWrapper><AdboardMapPage /></SuspenseWrapper>} />

      {/* Detail Pages */}
      <Route path="/listing-stay-detail/:id/*" element={<SuspenseWrapper><AdboardDetailPage /></SuspenseWrapper>} />
      <Route path="/hoardings/:title/:id/*" element={<SuspenseWrapper><AdboardDetailPage /></SuspenseWrapper>} />
      <Route path="/billboards/:title/:id/*" element={<SuspenseWrapper><AdboardDetailPage /></SuspenseWrapper>} />

      {/* Blog */}
      <Route path="/blog/*" element={<SuspenseWrapper><BlogPage /></SuspenseWrapper>} />
      <Route path="/blog-single/*" element={<SuspenseWrapper><BlogSingle /></SuspenseWrapper>} />
      <Route path="/blogs/*" element={<SuspenseWrapper><Blogs /></SuspenseWrapper>} />

      {/* Info Pages */}
      <Route path="/contact/*" element={<SuspenseWrapper><PageContact /></SuspenseWrapper>} />
      <Route path="/contact/:state/*" element={<SuspenseWrapper><PageContact /></SuspenseWrapper>} />
      <Route path="/about/*" element={<SuspenseWrapper><PageAbout /></SuspenseWrapper>} />
      <Route path="/subscription/*" element={<SuspenseWrapper><PageSubcription /></SuspenseWrapper>} />

      {/* Booking */}
      <Route path="/bookings/*" element={<SuspenseWrapper><BookingsPage /></SuspenseWrapper>} />
      <Route path="/bookings/:id/*" element={<SuspenseWrapper><BookingDetails /></SuspenseWrapper>} />
      <Route path="/booking/:id/*" element={<SuspenseWrapper><BookingDetails /></SuspenseWrapper>} />
      <Route path="/enquiries/*" element={<SuspenseWrapper><EnquiriesPage /></SuspenseWrapper>} />

      {/* Payments */}
      <Route path="/pay-done/*" element={<SuspenseWrapper><PayDonePage /></SuspenseWrapper>} />
      <Route path="/pay-done-campaign/:campaignId/*" element={<SuspenseWrapper><PayDoneCampaignPage /></SuspenseWrapper>} />

      {/* Media Assets */}
      <Route path="/media-assets/*" element={<SuspenseWrapper><MediaAssetsPage /></SuspenseWrapper>} />
      <Route path="/asset/:id/*" element={<SuspenseWrapper><MediaAsset /></SuspenseWrapper>} />

      {/* Notifications */}
      <Route path="/notifications/*" element={<SuspenseWrapper><NotificationsPage /></SuspenseWrapper>} />

      {/* 404 */}
      <Route path="*" element={<SuspenseWrapper><Page404 /></SuspenseWrapper>} />

      <Route path="/faqs" element={<SuspenseWrapper><FAQ /></SuspenseWrapper>} />
      <Route path="/campaigns" element={<SuspenseWrapper><Campaigns /></SuspenseWrapper>} />
      <Route path="/campaigns/new" element={<SuspenseWrapper><CampaignNew /></SuspenseWrapper>} />
      <Route path="/campaign/:id" element={<SuspenseWrapper><ManageCampaignPage /></SuspenseWrapper>} />
      <Route path="/campaigns/:id" element={<SuspenseWrapper><ManageCampaignPage /></SuspenseWrapper>} />
      <Route path="/blogs/:id" element={<SuspenseWrapper><Blogs /></SuspenseWrapper>} />
      <Route path="/wishlists" element={<SuspenseWrapper><WishlistsPage /></SuspenseWrapper>} />
      
    </ReactDomRoutes>
  );
}

const Routes = () => {
  const WIN_WIDTH = useWindowSize().width || window.innerWidth;
  
  return (
    <BrowserRouter>
      <StackTheme>
        <ScrollToTop />
        <SiteHeader />
        <PageRoutes />
        {WIN_WIDTH < 768 && <FooterNav />}
        <Footer />
      </StackTheme>
    </BrowserRouter>
  );
};

export default Routes;